import { useId, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ID_PROFILES,
  PATTERN_LABEL,
  LEG_TONE_LABEL,
  CONTEXT_LABEL,
  formatMm,
  getIdProfile,
  LEG_COUNT,
  identify,
  sizeFor,
  stageForSize,
  STAGE_LABEL,
  type BackPattern,
  type FoundContext,
  type IdAnswers,
  type IdMatch,
  type LegTone,
} from "@/lib/tick-id";
import { DEFAULT_PX_PER_MM } from "@/lib/screen-calibration";
import { scaleWidthPx } from "@/lib/size-scale";
import { track } from "@/lib/analytics";
import { CARD_SURFACE, WarningCard } from "@/components/page-shell";
import { cn } from "@/lib/utils";
import { SizeRuler } from "./size-ruler";
import { ScaleAxis, ScaleDrawing } from "./size-scale";
import { TickFigure } from "./tick-figure";

/** Sizes to offer, chosen to straddle the published bands rather than to look tidy. */
const SIZE_CHOICES = [
  { mm: 0.5, hint: "Pinhead" },
  { mm: 1.5, hint: "Poppy seed" },
  { mm: 3, hint: "Sesame seed" },
  { mm: 5, hint: "Apple seed" },
  { mm: 12, hint: "Pea" },
];

const PATTERNS: BackPattern[] = [
  "single-white-dot",
  "solid-dark-shield",
  "marbled-silver",
  "silver-stripes",
  "scattered-white-flecks",
  "plain-no-markings",
];

const LEG_TONES: LegTone[] = ["dark-contrasting", "same-as-body"];
const CONTEXTS: FoundContext[] = ["outdoors", "on-a-dog", "indoors"];

/** How each scored feature is named in the result: the margin label, then in a sentence. */
const FEATURE: Record<string, { term: string; phrase: string }> = {
  size: { term: "Size", phrase: "size" },
  pattern: { term: "Back", phrase: "markings" },
  legs: { term: "Legs", phrase: "leg colour" },
  context: { term: "Found", phrase: "where you found it" },
};

/** "a", "a and b", "a, b and c". */
function listJoin(items: string[]): string {
  if (items.length < 2) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** What the reader said, in the words of the button they pressed. */
function answerText(feature: string, answers: IdAnswers): string {
  switch (feature) {
    case "size": {
      const hint = SIZE_CHOICES.find((c) => c.mm === answers.sizeMm)?.hint;
      return hint ? `${hint}, about ${answers.sizeMm} mm` : `About ${answers.sizeMm} mm`;
    }
    case "pattern":
      return answers.pattern ? PATTERN_LABEL[answers.pattern] : "";
    case "legs":
      return answers.legTone ? LEG_TONE_LABEL[answers.legTone] : "";
    case "context":
      return answers.context ? CONTEXT_LABEL[answers.context] : "";
    default:
      return "";
  }
}

/**
 * Narrow down which tick it is.
 *
 * What this replaces had no useState anywhere: three hardcoded species in a shadow
 * interface, against the eight the site actually publishes, plus a life-stage diagram
 * drawn at the wrong proportions.
 *
 * It is a narrowing aid and says so everywhere. Nothing is eliminated, the score is a
 * count of matched features rather than a confidence percentage, and identify() returns
 * caveats — a fed tick, a larva, or a pair the sources say cannot be told apart by eye —
 * that are rendered above the result, not below it.
 *
 * Set like the key at the front of a field guide: numbered questions ruled between,
 * answers as square-shouldered toggles, and the verdict on a panel of its own.
 */
export function IdentifyFlow({
  pxPerMm,
  onCalibrate,
}: {
  pxPerMm: number;
  onCalibrate: (value: number) => void;
}) {
  const [answers, setAnswers] = useState<IdAnswers>({});

  const result = useMemo(() => identify(answers), [answers]);
  const scaleWidth = scaleWidthPx(pxPerMm);
  const calibrated = pxPerMm !== DEFAULT_PX_PER_MM;
  const answeredAny = result.answered > 0 || answers.engorged !== undefined;
  const engorged = answers.engorged ?? false;

  const set = <K extends keyof IdAnswers>(key: K, value: IdAnswers[K]) => {
    setAnswers((prev) => {
      const next = { ...prev };
      if (prev[key] === value) delete next[key];
      else next[key] = value;
      track("tick_id_answered", { question: key });
      return next;
    });
  };

  const best = result.matches[0]?.matched ?? 0;
  const top = answeredAny && best > 0 ? result.matches.filter((m) => m.matched === best) : [];
  const others = answeredAny && best > 0 ? result.matches.filter((m) => m.matched < best) : [];

  // The markings question drops out for a fed tick, so the key renumbers around it.
  let n = 0;
  const num = () => ++n;

  return (
    <div className="space-y-10">
      <section aria-labelledby="id-key-heading" className="border-t border-rule/70 pt-6">
        <h2
          id="id-key-heading"
          className="font-display text-[1.75rem] font-medium leading-tight text-foreground"
        >
          What can you see?
        </h2>
        <p className="measure mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">
          Answer any of these, in any order. The result updates as you go, and tapping an answer
          again clears it.
        </p>

        <div className="mt-6 border-b border-border">
          <Question
            n={num()}
            legend="How big is it, roughly?"
            hint="Tap the closest. Each tick lies on the same millimetre ruler, with its head to the right."
          >
            <div className="max-w-md">
              <div className="grid gap-1.5">
                {SIZE_CHOICES.map((choice) => (
                  <Choice
                    key={choice.mm}
                    selected={answers.sizeMm === choice.mm}
                    onClick={() => set("sizeMm", choice.mm)}
                    className="flex w-full items-center gap-3 py-1 pl-2 pr-4 text-left"
                  >
                    <ScaleDrawing
                      mm={choice.mm}
                      pxPerMm={pxPerMm}
                      width={scaleWidth}
                      engorged={choice.mm >= 10}
                    />
                    <span className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-3">
                      <span>{choice.hint}</span>
                      <span className="tabular text-xs font-normal text-muted-foreground">
                        {choice.mm} mm
                      </span>
                    </span>
                  </Choice>
                ))}
              </div>
              <div className="mt-1 pl-[calc(0.5rem+1px)]">
                <ScaleAxis pxPerMm={pxPerMm} width={scaleWidth} />
              </div>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {calibrated
                  ? "Drawn at real size for your screen. The circles show the smallest ones enlarged."
                  : "About real size on most screens. The circles show the smallest ones enlarged."}
              </p>
            </div>

            {/* Optional, not a gate: the size buttons carry everyday names ("sesame seed"),
                so the key works uncalibrated. V3 opened with this step, which put a slider
                and a coin between a worried reader and the first question. */}
            <details className="mt-4">
              <summary className="w-fit cursor-pointer py-2 text-sm font-semibold text-primary hover:underline">
                Draw these at real size on your screen
              </summary>
              <div className="mt-2">
                <SizeRuler pxPerMm={pxPerMm} onChange={onCalibrate} />
              </div>
            </details>
          </Question>

          <Question
            n={num()}
            legend="Is it flat, or swollen with blood?"
            hint="A tick that has fed changes colour and shape, and its markings stretch out."
          >
            <div className="flex flex-wrap gap-2">
              <Choice selected={answers.engorged === false} onClick={() => set("engorged", false)}>
                Flat, not fed
              </Choice>
              <Choice selected={answers.engorged === true} onClick={() => set("engorged", true)}>
                Swollen and rounded
              </Choice>
            </div>
          </Question>

          {!engorged && (
            <Question
              n={num()}
              legend="What does its back look like?"
              hint="The most useful thing to look at on an unfed tick."
            >
              <div className="grid gap-2 sm:grid-cols-2">
                {PATTERNS.map((pattern) => (
                  <Choice
                    key={pattern}
                    selected={answers.pattern === pattern}
                    onClick={() => set("pattern", pattern)}
                    className="flex w-full items-center gap-3 py-2 pl-2.5 text-left"
                  >
                    <span className="grid h-10 w-10 shrink-0 place-items-center">
                      <TickFigure mm={10} pxPerMm={3.6} pattern={pattern} />
                    </span>
                    <span>{PATTERN_LABEL[pattern]}</span>
                  </Choice>
                ))}
              </div>
            </Question>
          )}

          <Question n={num()} legend="What colour are its legs?">
            <div className="flex flex-wrap gap-2">
              {LEG_TONES.map((tone) => (
                <Choice
                  key={tone}
                  selected={answers.legTone === tone}
                  onClick={() => set("legTone", tone)}
                >
                  {LEG_TONE_LABEL[tone]}
                </Choice>
              ))}
            </div>
          </Question>

          <Question n={num()} legend="Where did you find it?">
            <div className="flex flex-wrap gap-2">
              {CONTEXTS.map((context) => (
                <Choice
                  key={context}
                  selected={answers.context === context}
                  onClick={() => set("context", context)}
                >
                  {CONTEXT_LABEL[context]}
                </Choice>
              ))}
            </div>
          </Question>
        </div>

        {/* Answers update the result live, but on a phone the result is four questions
            below the thumb. This keeps the current best guess in view until you reach it.
            It is a zero-height sticky row at the foot of the key, so it takes no room in
            the page and goes with the key once you scroll past it to the result. */}
        {answeredAny && top.length > 0 && (
          <div className="no-print sticky bottom-4 z-10 mt-4 flex h-0 items-end justify-center">
            <a
              href="#id-results"
              className="max-w-full rounded-sm bg-foreground px-4 py-2.5 text-sm font-semibold text-background hover:bg-primary hover:text-primary-foreground"
            >
              {top.length === 1
                ? `Best match so far: ${getIdProfile(top[0].slug)?.commonName ?? "see below"}`
                : `${top.length} ticks fit so far`}
            </a>
          </div>
        )}
      </section>

      {answeredAny && (
        <Results
          top={top}
          others={others}
          answers={answers}
          caveats={result.caveats}
          answered={result.answered}
          pxPerMm={pxPerMm}
          impliedStage={result.impliedStage}
          onReset={() => setAnswers({})}
        />
      )}
    </div>
  );
}

/**
 * One couplet of the key: its number in the headline face, the question, and the
 * answers. A labelled group rather than a <fieldset>, so the legend is not notched into
 * a box border.
 */
function Question({
  n,
  legend,
  hint,
  children,
}: {
  n: number;
  legend: string;
  hint?: string;
  children: React.ReactNode;
}) {
  const id = useId();
  return (
    <div
      role="group"
      aria-labelledby={`${id}-q`}
      aria-describedby={hint ? `${id}-h` : undefined}
      className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-x-3 border-t border-border py-6 sm:grid-cols-[3rem_minmax(0,1fr)] sm:gap-x-4 sm:py-7"
    >
      <span
        aria-hidden
        className="tabular font-display text-[1.75rem] leading-[1.1] text-rust-ink sm:text-[2rem]"
      >
        {n}
      </span>
      <div>
        <h3
          id={`${id}-q`}
          className="pt-1 font-display text-xl font-medium leading-snug text-foreground"
        >
          {legend}
        </h3>
        {hint && (
          <p id={`${id}-h`} className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {hint}
          </p>
        )}
      </div>
      <div className="col-span-2 mt-4 sm:col-span-1 sm:col-start-2">{children}</div>
    </div>
  );
}

/**
 * A toggle in the key. Selected is drawn in ink: a doubled dark edge, the sage fill and
 * bold type, so it reads at a glance and in greyscale, not by colour alone.
 */
function Choice({
  selected,
  onClick,
  children,
  className,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        "min-h-11 rounded-sm border px-4 py-2.5 text-sm leading-snug text-foreground transition-colors",
        selected
          ? "border-foreground bg-secondary font-semibold ring-1 ring-inset ring-foreground"
          : "border-border bg-card hover:border-foreground/50",
        className,
      )}
    >
      {children}
    </button>
  );
}

function Results({
  top,
  others,
  answers,
  caveats,
  answered,
  pxPerMm,
  impliedStage,
  onReset,
}: {
  top: IdMatch[];
  others: IdMatch[];
  answers: IdAnswers;
  caveats: string[];
  answered: number;
  pxPerMm: number;
  impliedStage?: ReturnType<typeof identify>["impliedStage"];
  onReset: () => void;
}) {
  const stage = impliedStage ?? (answers.sizeMm ? stageForSize(answers.sizeMm) : undefined);
  const engorged = answers.engorged ?? false;
  const sizeOf = (slug: string) => {
    const profile = getIdProfile(slug);
    return profile && stage ? sizeFor(profile, stage, engorged) : undefined;
  };
  const matchesLine = (match: IdMatch) => {
    const size = sizeOf(match.slug);
    return (
      <>
        Matches {match.matched} of the {answered} {answered === 1 ? "thing" : "things"} you
        described
        {size && stage ? ` · ${STAGE_LABEL[stage].toLowerCase()} ${formatMm(size)}` : ""}
      </>
    );
  };

  const single = top.length === 1 ? top[0] : undefined;

  return (
    <section id="id-results" aria-live="polite" className="scroll-mt-20">
      <div
        className={cn(
          CARD_SURFACE,
          "border-t-[3px] border-t-foreground px-5 pb-5 pt-4 sm:px-8 sm:pb-7 sm:pt-6",
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <h2
            className={
              single
                ? "label-caps text-muted-foreground"
                : "font-display text-[1.75rem] font-medium leading-tight text-foreground"
            }
          >
            {top.length === 0
              ? answered === 0
                ? "Nothing to compare yet"
                : "Nothing matches what you've described"
              : single
                ? "Most consistent with"
                : `${top.length} ticks fit equally well`}
          </h2>
          <button
            type="button"
            onClick={onReset}
            className="link inline-flex min-h-11 shrink-0 items-center text-sm font-semibold"
          >
            Start over
          </button>
        </div>

        {top.length === 0 && answered === 0 && (
          <p className="mt-1 text-sm text-muted-foreground">
            Answer another question above to see which ticks fit.
          </p>
        )}

        {caveats.length > 0 && (
          <div className="mt-4 space-y-3">
            {caveats.map((caveat) => (
              <WarningCard key={caveat}>{caveat}</WarningCard>
            ))}
          </div>
        )}

        {single && (
          <Verdict
            match={single}
            answers={answers}
            pxPerMm={pxPerMm}
            stage={stage}
            line={matchesLine(single)}
          />
        )}

        {top.length > 1 && (
          <ul className="mt-5 divide-y divide-border border-t border-border">
            {top.map((match) => (
              <li key={match.slug} className="py-4">
                <h3 className="font-display text-xl font-medium text-foreground">
                  {getIdProfile(match.slug)!.commonName}
                </h3>
                <p className="mt-0.5 text-sm text-muted-foreground">{matchesLine(match)}</p>
                {match.matchedOn.length > 0 && (
                  <p className="mt-1 text-sm text-foreground">
                    Fits on {listJoin(match.matchedOn.map((f) => FEATURE[f]?.phrase ?? f))}.
                  </p>
                )}
                <Link
                  to="/ticks/$species"
                  params={{ species: match.slug }}
                  className="link mt-2 inline-block text-sm font-semibold"
                >
                  Full profile and sources
                </Link>
              </li>
            ))}
          </ul>
        )}

        {others.length > 0 && (
          <details className={cn("border-t border-border", single && "mt-6")}>
            <summary className="cursor-pointer py-3.5 text-sm font-semibold text-foreground">
              <span>The other {others.length} we considered</span>
            </summary>
            <ul className="divide-y divide-border border-y border-border">
              {others.map((match) => (
                <li
                  key={match.slug}
                  className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 py-2.5 text-[0.9375rem]"
                >
                  <Link to="/ticks/$species" params={{ species: match.slug }} className="link">
                    {getIdProfile(match.slug)!.commonName}
                  </Link>
                  <span className="tabular text-sm text-muted-foreground">
                    matches {match.matched} of {answered}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      <div className="mt-6 flex flex-col items-start gap-x-10 gap-y-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="measure text-sm leading-relaxed text-muted-foreground">
          <strong className="font-semibold text-foreground">
            This narrows the list. It is not an identification.
          </strong>{" "}
          These are stylised shapes, not photographs, and several species are hard to tell apart
          without magnification. If it matters, seal the tick in a bag and ask your state health
          department or a university extension program.
        </p>
        <Link
          to="/tick-bite-tool"
          className="inline-flex shrink-0 rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary-dark"
        >
          It was attached to me
        </Link>
      </div>
    </section>
  );
}

/** The one-species answer: the name large, a drawing to scale, and why it fits. */
function Verdict({
  match,
  answers,
  pxPerMm,
  stage,
  line,
}: {
  match: IdMatch;
  answers: IdAnswers;
  pxPerMm: number;
  stage?: ReturnType<typeof identify>["impliedStage"];
  line: React.ReactNode;
}) {
  const profile = getIdProfile(match.slug)!;
  const engorged = answers.engorged ?? false;
  const size = stage ? sizeFor(profile, stage, engorged) : undefined;
  const calibrated = pxPerMm !== DEFAULT_PX_PER_MM;

  return (
    <div className="mt-3 grid gap-x-10 md:grid-cols-[minmax(0,1fr)_auto]">
      <div className="min-w-0">
        <h3 className="font-display text-[2.25rem] font-medium leading-[1.05] tracking-[-0.015em] text-foreground sm:text-[2.75rem]">
          {profile.commonName}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">{line}</p>
      </div>

      {/* A field guide's pair of drawings: one enlarged to show the marks, one beside it
          at life size to show how little there is to see. */}
      {size && (
        <figure className="m-0 mt-5 md:col-start-2 md:row-span-2 md:row-start-1 md:mt-1">
          <div className="flex w-fit items-end border-b border-foreground/50">
            <span className="flex h-24 w-24 items-end justify-center pb-1.5">
              <TickFigure
                mm={10}
                pxPerMm={8.4}
                pattern={profile.patterns[0]}
                darkLegs={profile.legTone === "dark-contrasting"}
                engorged={engorged}
                legs={stage ? LEG_COUNT[stage] : 8}
              />
            </span>
            <span className="flex h-24 w-20 items-end justify-center pb-1.5">
              <TickFigure
                mm={Math.min(14, (size.minMm ?? size.maxMm ?? 3) as number)}
                pxPerMm={pxPerMm}
                pattern={profile.patterns[0]}
                darkLegs={profile.legTone === "dark-contrasting"}
                engorged={engorged}
                legs={stage ? LEG_COUNT[stage] : 8}
              />
            </span>
          </div>
          <figcaption className="flex text-center text-xs text-muted-foreground">
            <span className="w-24 pt-1.5">Enlarged</span>
            <span className="w-20 pt-1.5">{calibrated ? "Life size" : "About life size"}</span>
          </figcaption>
        </figure>
      )}

      <div className="min-w-0 md:col-start-1">
        {match.matchedOn.length > 0 && (
          <div className="mt-5">
            <p className="label-caps text-muted-foreground">What fits</p>
            <dl className="mt-2 divide-y divide-border border-y border-border">
              {match.matchedOn.map((feature) => (
                <div
                  key={feature}
                  className="grid grid-cols-[4.25rem_minmax(0,1fr)] gap-x-4 py-2.5 sm:grid-cols-[5rem_minmax(0,1fr)]"
                >
                  <dt className="label-caps pt-[0.3em] text-muted-foreground">
                    {FEATURE[feature]?.term ?? feature}
                  </dt>
                  <dd className="text-[0.9375rem] leading-snug text-foreground">
                    {answerText(feature, answers)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
        {match.missedOn.length > 0 && (
          <p className="mt-3 text-sm text-muted-foreground">
            Does not fit what you said about{" "}
            {listJoin(match.missedOn.map((f) => FEATURE[f]?.phrase ?? f))}.
          </p>
        )}

        <Link
          to="/ticks/$species"
          params={{ species: profile.slug }}
          className="link mt-4 inline-block text-sm font-semibold"
        >
          Full profile and sources
        </Link>
      </div>
    </div>
  );
}

/** All eight profiled species at their real adult size, for the reference strip. */
export function SpeciesSizeStrip({ pxPerMm }: { pxPerMm: number }) {
  return (
    <ul className="flex flex-wrap items-end gap-x-6 gap-y-4">
      {ID_PROFILES.map((profile) => (
        <li key={profile.slug} className="flex flex-col items-center text-center">
          <span className="grid h-16 w-16 place-items-center">
            <TickFigure
              mm={profile.stages.adult.maxMm ?? profile.stages.adult.minMm ?? 3}
              pxPerMm={pxPerMm}
              pattern={profile.patterns[0]}
              darkLegs={profile.legTone === "dark-contrasting"}
            />
          </span>
          <span className="mt-1 max-w-[7rem] text-xs font-semibold text-foreground">
            {profile.commonName}
          </span>
          <span className="text-xs text-muted-foreground">{formatMm(profile.stages.adult)}</span>
        </li>
      ))}
    </ul>
  );
}
